import { Body, Box, Image, useToast } from '@hover/blueprint';
import React, { useMemo, useState } from 'react';
import { Trans } from 'react-i18next';

import { LineBreak } from 'src/modules/designOrders/components';
import { PROJECT_DETAILS_PADDING } from 'src/modules/homeownerApp/components/Projects/Project/ProjectCardSection';
import { useScanImage } from 'src/modules/homeownerApp/components/Projects/Project/hooks';
import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { useAttachScanToProjectHandler } from 'src/modules/homeownerApp/hooks/useAttachScanToProjectHandler';
import { Button } from 'src/shared/components';
import type { BasicJobInfoFragment } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

interface MostRecentScanProps {
  scan: BasicJobInfoFragment;
  projectId: string;
  spaceType?: string;
}

export const MostRecentScan = ({
  scan,
  projectId,
  spaceType = 'Exterior',
}: MostRecentScanProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { trackingData } = useHomeownerAppContext();
  const toast = useToast({ type: 'error', position: 'top' });
  const { name, images, locationLine1, bestFrontImage } = scan;
  const displayImage = useScanImage(bestFrontImage, images) ?? undefined;
  const [attaching, setAttaching] = useState(false);
  const [attachScanToProjectHandler] = useAttachScanToProjectHandler(
    projectId,
    scan,
  );

  const scanTrackingData = useMemo(
    () => ({
      ...trackingData,
      job_id: scan.id,
      button_location: 'Measurement tile',
      track_id: 'add-scan-to-project',
      project_id: Number(projectId),
    }),
    [trackingData, scan.id, projectId],
  );

  const handleAddToProject = async () => {
    try {
      setAttaching(true);
      await attachScanToProjectHandler();
      setAttaching(false);
    } catch {
      setAttaching(false);
      toast({ props: { message: t('projects.scan.attachError') } });
    }
  };

  return (
    <Box flexDirection="column" m={0} px={PROJECT_DETAILS_PADDING}>
      <Body fontWeight={700} m={0} p={0} size={300}>
        {t('projects.scan.header')}
      </Body>
      <Body color="neutral.600" m={0} p={0} size={300}>
        <Trans
          components={{ LineBreak: <LineBreak /> }}
          i18nKey="projects.scan.description"
          t={t}
        />
      </Body>
      <Box
        alignItems="center"
        backgroundColor="primary.100"
        borderRadius={400}
        flexDirection={{ base: 'column', tablet: 'row' }}
        flexWrap="wrap"
        gap={500}
        mt={500}
        p={500}
        w="100%"
      >
        <Box flex={1} gap={500} width="100%">
          <Image
            alt="scan"
            borderRadius={300}
            fallback={
              <Box backgroundColor="neutral.100" borderRadius={300} h="80px" />
            }
            h="80px"
            src={displayImage}
            w="80px"
          />
          <Box flex={1} flexDirection="column">
            <Body fontWeight={700} lineHeight={400} m={0} p={0} size={300}>
              {name}
            </Body>
            <Body lineHeight={400} m={0} p={0} size={300}>
              {spaceType}
            </Body>
            <Body lineHeight={400} m={0} noOfLines={1} p={0} size={300}>
              {locationLine1}
            </Body>
          </Box>
        </Box>
        <Button
          disabled={attaching}
          includeTrackingImpression
          isLoading={attaching}
          label={t('projects.addToProject')}
          onClick={handleAddToProject}
          optionalTrackingProperties={scanTrackingData}
          width={{ base: '100%', tablet: 'auto' }}
        >
          {t('projects.addToProject')}
        </Button>
      </Box>
    </Box>
  );
};
